// @flow
import React, {Component, type Node} from 'react';
import {throttle} from 'lodash';

type Rect = {
    top: number,
    left: number,
    right: number,
    bottom: number,
    width: number,
    height: number
};

type Props = {
    useWindowAsScrollParent: ?boolean,
    onScroll: Function,
    throttleAmount: number,
    children: Node | ((?Rect) => Node)
};

type State = {
    rect: ?Rect
};

/**
 * Since scroll events will not always be on the window, we need to find the
 * container that is handling the scroll event, and hook up to that.
 *
 * This function walks up the tree and finds the containing element that should
 * be handling that event.
 */
export function getScrollParent(element: any) {
    let style = window.getComputedStyle(element);
    const excludeStaticParent = style.position === 'absolute';
    const overflowRegex = /(auto|scroll)/;

    if (style.position === 'fixed') {
        return window;
    }

    while (element.parentElement) {
        element = element.parentElement;
        style = window.getComputedStyle(element);
        if (excludeStaticParent && style.position === 'static') {
            continue;
        }
        if (
            overflowRegex.test(
                style.overflow + style.overflowY + style.overflowX
            )
        ) {
            return element;
        }
    }

    return window;
}

/**
 * Component that listens for scroll events on the closest scrollable parent
 * element. If passed a children function, will call children with the
 * boundingClientRect of the ScrollListener's ref'd div.
 *
 * TODO: for scrollParents other than the window object, consider passing
 * children the scrollParent's scrollTop property instead.
 */
class ScrollListener extends Component<Props, State> {
    elem: any;
    ref: {current: null | HTMLDivElement};
    scrollParent: Element;

    static defaultProps = {
        onScroll: () => {},
        throttleAmount: 100
    };

    constructor(props: Props) {
        super(props);
        this.state = {rect: null};
        this.ref = React.createRef();
    }

    componentDidMount() {
        this.elem = this.ref.current;

        this.updateRect();

        // Determine what element (or window) scrolls
        this.scrollParent = this.props.useWindowAsScrollParent
            ? window
            : getScrollParent(this.elem);
        /* istanbul ignore next */
        if (this.elem === this.scrollParent && this.elem instanceof Element) {
            this.elem = this.elem.firstChild;
        }
        this.scrollParent.addEventListener('scroll', this.throttledHandler);
    }

    componentWillUnmount() {
        this.detachListeners();
    }

    updateRect() {
        if (typeof this.props.children === 'function' && this.ref.current) {
            const rect = this.ref.current.getBoundingClientRect();

            // iOS gave me some squirrely almost-but-not-quite zero values, so
            // use a very fast bitwise operation to truncate any fractional
            // values off these pixel measurements
            // c.f. https://stackoverflow.com/questions/38702724/math-floor-vs-math-trunc-javascript
            // c.f. https://eslint.org/docs/rules/no-bitwise#int32hint

            /* eslint no-bitwise: ["error", { "int32Hint": true }] */
            this.setState({
                rect: {
                    top: rect.top | 0,
                    left: rect.left | 0,
                    right: rect.right | 0,
                    bottom: rect.bottom | 0,
                    width: rect.width | 0,
                    height: rect.height | 0
                }
            });
        } else {
            this.setState({rect: null});
        }
    }

    detachListeners = () => {
        this.scrollParent.removeEventListener('scroll', this.throttledHandler);
    };

    scrollListener = () => {
        this.updateRect();
        this.props.onScroll();
    };

    throttledHandler = throttle(this.scrollListener, this.props.throttleAmount);

    render() {
        const {children, onScroll, throttleAmount, ...rest} = this.props; // eslint-disable-line no-unused-vars
        return (
            <div ref={this.ref} {...rest}>
                {typeof children === 'function'
                    ? children(this.state.rect)
                    : children}
            </div>
        );
    }
}

export default ScrollListener;
